import { useCallback, useEffect, useState } from "react";
import { Select } from "@/components/tailwind/select";
import { District } from "@/models/district";
import { fetchAllDistricts } from "@/services/district-services";
import { useAuth } from "@/context/use-auth";

interface DistrictSelectorProps {
    value: string;
    onChange: (districtId: string) => void;
    id?: string;
    label?: string;
    placeholder?: string;
    'aria-label'?: string;
    'data-testid'?: string;
}

export function DistrictSelector({
    value,
    onChange,
    id,
    placeholder = "",
    'aria-label': ariaLabel,
    'data-testid': dataTestId,
}: DistrictSelectorProps) {
    const { token } = useAuth();
    const [districts, setDistricts] = useState<District[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (token) {
            setIsLoading(true);
            fetchAllDistricts(token)
                .then((result) => {
                    if (result) setDistricts(result);
                })
                .finally(() => setIsLoading(false));
        }
    }, [token]);

    return (
        <Select
            id={id}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            aria-label={ariaLabel}
            data-testid={dataTestId}
            disabled={isLoading}
        >
            <option value="">{placeholder}</option>
            {districts.map((d) => (
                <option key={d.id} value={d.id}>{d.name}</option>
            ))}
        </Select>
    );
}

export function useDistrictSuggestion(initialDistrictId: string = "") {
    const [selectedDistrictId, setSelectedDistrictId] = useState<string>(initialDistrictId);
    const [districtManuallySet, setDistrictManuallySet] = useState(false);

    const handleDistrictChange = useCallback((districtId: string) => {
        setSelectedDistrictId(districtId);
        setDistrictManuallySet(true);
    }, []);

    const handleSuggestionSelect = useCallback((districtId: string) => {
        if (!districtManuallySet) {
            setSelectedDistrictId(districtId);
        }
    }, [districtManuallySet]);

    const resetDistrict = useCallback((districtId: string = "") => {
        setSelectedDistrictId(districtId);
        setDistrictManuallySet(false);
    }, []);

    return {
        selectedDistrictId,
        handleDistrictChange,
        handleSuggestionSelect,
        resetDistrict,
    };
}
